/**
 * Synthesize canvas JSON from a design placement.
 *
 * The inverse of the bounding box (`placementMmFromCanvasJson`): it turns a
 * stored placement plus a crop image into the single-image canvas JSON a
 * Fabric editor would have produced for the same placement.
 *
 * Callers:
 *
 * - The design load path and the external design read, for rows created by
 *   batch-apply, which carry a `placementMm` and a crop but no canvas JSON.
 * - Any placement authored without a mounted canvas (preset picking), which
 *   still has to produce real canvas JSON — the step guards and the save path
 *   both reject a position whose `json` is null or has no objects.
 *
 * `canvasJSONFromPlacementMm` writes the stored format: millimetre space,
 * no clip (R34); the web editor converts it to its template's pixels like
 * every loaded row.
 *
 * Pure: no Fabric, no DOM, no I/O.
 */

import type { PlacementMm } from '../placement/placementMm.js';
import type { CanvasObjectJSON } from './computeDesignState.js';

/**
 * Natural pixel size of the image behind `cropUrl`.
 *
 * Fabric does NOT treat an image object's `width`/`height` as a nominal box it
 * rescales the bitmap into: it reads them as the source rectangle it cuts out
 * of the element (the same fields `cropX`/`cropY` offset). Declaring anything
 * other than the element's true size therefore paints only that fraction of the
 * object — a 784 px image in a declared 1000 px object fills 78% of its own
 * selection box, handles included. The true dimensions are the only correct
 * value here, which is why the caller has to supply them.
 */
export interface SynthesizedImageSource {
  width: number;
  height: number;
}

/** The single image object of the stored (millimetre) format: no clip. */
export interface SynthesizedImageObjectMm extends CanvasObjectJSON {
  type: 'image';
  src: string;
}

export interface SynthesizedCanvasJSONMm {
  version: string;
  objects: SynthesizedImageObjectMm[];
}

/**
 * Canvas JSON in millimetre space placing `cropUrl` exactly on `placement`
 * (millimetres from the area's top left). Its bounding box is the placement
 * on both axes, so it reads back as the same `placementMm`.
 *
 * @throws when the placement, the crop or the image source is degenerate.
 */
export function canvasJSONFromPlacementMm(
  placement: PlacementMm,
  cropUrl: string,
  source: SynthesizedImageSource,
  /**
   * Further properties for the object — Fabric custom properties such as an
   * embroidery motif's `embFileKey` and `confirmedMapping`, which the publish
   * rules read off the object exactly as the editor would have written them.
   * Geometry and source fields cannot be overridden.
   */
  objectProps: Record<string, unknown> = {}
): SynthesizedCanvasJSONMm {
  if (!(placement.width > 0) || !(placement.height > 0)) {
    throw new Error(
      `canvasJSONFromPlacementMm: degenerate placement ${placement.width}x${placement.height}`
    );
  }
  if (!cropUrl) {
    throw new Error(
      'canvasJSONFromPlacementMm: cropUrl is required as the object src'
    );
  }
  if (!(source.width > 0) || !(source.height > 0)) {
    throw new Error(
      `canvasJSONFromPlacementMm: degenerate image source ${source.width}x${source.height}`
    );
  }
  return {
    version: '6.0.0',
    objects: [
      {
        ...objectProps,
        type: 'image',
        left: placement.x + placement.width / 2,
        top: placement.y + placement.height / 2,
        width: source.width,
        height: source.height,
        scaleX: placement.width / source.width,
        scaleY: placement.height / source.height,
        originX: 'center',
        originY: 'center',
        src: cropUrl,
      },
    ],
  };
}
